import * as React from "react";
import { CircularProgress, styled } from "@mui/material";
import { AGENT_CASE_API_KEY } from "fe-common/commonTypes/masterClinicDefaults";
import useLoadScript from "fe-common/hooks/useLoadScript";
import { isProduction } from "fe-common/utils/envGetter";
import { useEffect, useRef, useState } from "react";
import { CaseStatus } from "@kahunmedical/common-wtypes";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { scrollableStyle } from "fe-common/ui/commonStyles";

declare namespace Kahun {
  function newCaseRecord(params: NewCaseParams): Promise<CaseRecord>;
  function existingCaseRecord(params: ExistingCaseParams): Promise<CaseRecord>;
}
//region [[ Styles ]]

const NewCaseChatView = styled("div")({
  height: 670,
  width: 540,
  background: "#FFFFFF",
  position: "relative",
});
const ChatWrapper = styled("div")({
  width: "100%",
  height: "100%",
  position: "relative",
  "& .new-chat-loader": {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -40,
    marginLeft: -40,
  },
  "& .kahun-patient": {
    height: "100%",
  },
  ...scrollableStyle,
});
const RedirectingMessage = styled("div")({
  position: "absolute",
  top: -45,
  right: 18,
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  gap: 8,
});

//endregion [[ Styles ]]

//region [[ Props ]]
interface ExistingCaseParams {
  apiKey: string; // the APIKEY provided to you
  clinicId: string; // the CLINIC_ID provided to you
  caseId: string; // the case id to use
}
interface NewCaseParams {
  apiKey: string; // the APIKEY provided to you
  clinicId: string; // the CLINIC_ID provided to you
  clinicalData?: object; // optionally, background data about the patient
}
interface WidgetSettings {
  locale?: string; // the display language for chatbot
  onCompletionUrl?: string; // if provided, will redirect at end of conversation
  onAbandonUrl?: string; // if provided, will redirect when conversation ends prematurely
  allowClosing: boolean;
  nonPersist?: boolean; // can change to a non-persist mode
  termsAgreed: boolean;
  navigationBar: boolean;
}
interface CaseRecord {
  getCaseId(): string; // the id of the case
  getLinks(): Record<string, string>; // get urls for summary,pdf,patient,and provider web resources
  getStatus(): CaseStatus; //"ABANDONED" | "COMPLETED" | "IN_PROGRESS" | "CREATED";
  generateSummary(): Promise<object>; // generate and receive summary output
  startChatBotWidget(settings?: WidgetSettings): Promise<boolean>; // launch the chatbot as a page widget
  removeChatBotWidget(): void; // remove the chatbot widget from the page
  subscribe(name: string, cb: any); // register for events
  release(name: string, cb: any); // unregister for events
}
export interface DemoCaseChatProps {
  clinicId: string;
  onSave: (caseId: string) => void;
  caseId?: string;
  onNewCase?: (newId: string) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const NewCaseChat = ({
  clinicId,
  onSave,
  caseId,
  onNewCase,
}: DemoCaseChatProps) => {
  const [redirecting, setRedirecting] = useState(false);
  const clinicDao = useClinicContext();

  const widgetIsOpen = useRef(false);

  const { isInit } = useLoadScript({
    url: isProduction()
      ? "https://patient.kahun.com/api/clientapi.js"
      : "https://patient-dev.kahun.com/api/clientapi.js",
    async: true,
  });

  useEffect(() => {
    let caseRecord: CaseRecord;

    (async () => {
      if (!isInit) return;
      if (widgetIsOpen.current) return;
      let chatCaseId: string = caseId;
      if (!caseId) {
        caseRecord = await Kahun.newCaseRecord({
          clinicId: clinicId,
          apiKey: AGENT_CASE_API_KEY,
        });
        chatCaseId = caseRecord.getCaseId();
        onNewCase(chatCaseId);
      } else {
        caseRecord = await Kahun.existingCaseRecord({
          clinicId,
          caseId: chatCaseId,
          apiKey: AGENT_CASE_API_KEY,
        });
      }
      caseRecord.subscribe("done", async () => {
        setRedirecting(true);
        const caseById = await clinicDao.getCodeFromShareId(
          clinicId,
          chatCaseId,
        );
        onSave(caseById.digitCode);
      });
      widgetIsOpen.current = true;
      if (caseId) {
        await caseRecord.startChatBotWidget({
          allowClosing: false,
          nonPersist: false,
          termsAgreed: false,
          // onCompletionUrl: caseUrl,
          // onAbandonUrl: caseUrl,
          navigationBar: false,
        });
      }
    })();
    return () => {
      widgetIsOpen.current = false;
      if (caseRecord) caseRecord.removeChatBotWidget();
    };
  }, [isInit, clinicId, onSave, clinicDao, caseId, onNewCase]);

  // @ts-ignore
  return (
    <NewCaseChatView>
      {redirecting && (
        <RedirectingMessage>
          <CircularProgress size={18} />
          Opening patient card...
        </RedirectingMessage>
      )}
      <ChatWrapper>
        <div
          id="kahun-patient"
          className="kahun-patient"
          data-window-alignment="inline"
          key={"kahun-patient-chat"}
        >
          <CircularProgress size={80} className={"new-chat-loader"} />
        </div>
      </ChatWrapper>
    </NewCaseChatView>
  );
};
